// ABOUTME: The eight mini weather sprites drawn at the top of the head cloud, keyed by weather code.
// ABOUTME: Each is a small hand-built artboard (48 x 30) from the Cloud Stack Build Spec handoff; an unknown code draws nothing.
import { Box } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { useCrossingReducedMotion } from '../../crossing/useReducedMotion';
import { WR_RIDE_ANIMATION, WR_RIDE_KEYFRAMES } from '../../crossing/wrRide';

const CANOE_SRC = '/nalu/logo-waa-256.png';
const HALO = 'drop-shadow(0 0 5px rgba(255,255,255,.35)) drop-shadow(0 0 12px rgba(255,255,255,.22))';
const hatch = (angle: number, rgb: string, alpha: number) =>
  `repeating-linear-gradient(${angle}deg, rgba(${rgb},${alpha}) 0 1px, transparent 1px 3px)`;

// One drawn piece, positioned on the 48 x 30 artboard in px.
interface Piece {
  x: number;
  y: number;
  w: number;
  h: number;
  sx?: Record<string, string | number>;
  children?: Piece[];
  img?: boolean;
  glyph?: string;
}

const pill = (x: number, y: number, w: number, h: number, background: string): Piece =>
  ({ x, y, w, h, sx: { borderRadius: '999px', background } });
const oval = (x: number, y: number, w: number, h: number, background: string): Piece =>
  ({ x, y, w, h, sx: { borderRadius: '50%', background } });
const bar = (x: number, y: number, w: number, background: string): Piece =>
  ({ x, y, w, h: 1.5, sx: { borderRadius: '1px', background } });
const hatched = (x: number, y: number, w: number, h: number, angle: number, rgb: string, alpha: number): Piece =>
  ({ x, y, w, h, sx: { background: hatch(angle, rgb, alpha) } });

const TEAL = '46,110,100';

const SPRITES: Record<string, Piece[]> = {
  got_big_and_loud: [
    pill(11, 11, 24, 8, '#F0B3A0'),
    oval(16, 5, 12, 9, '#F3BFAE'),
    oval(23, 7, 9, 7, '#F6CDBF'),
  ],
  took_the_wheel: [
    { x: 16, y: 2, w: 14, h: 16, sx: { background: `linear-gradient(rgba(${TEAL},0.1), rgba(${TEAL},0.5))`, clipPath: 'polygon(40% 0, 60% 0, 100% 100%, 0 100%)' } },
    bar(19, 9, 8, 'rgba(255,255,255,0.7)'),
    bar(18, 12, 10, 'rgba(255,255,255,0.55)'),
    {
      x: 16, y: 11, w: 15, h: 8, sx: { overflow: 'hidden' },
      children: [
        { x: 0, y: 0, w: 15, h: 15, sx: { borderRadius: '50%', border: `1.2px solid rgba(${TEAL},0.6)`, boxSizing: 'border-box' } },
        { x: 7.2, y: 0, w: 1.2, h: 8, sx: { background: `rgba(${TEAL},0.55)` } },
        { x: 7.2, y: 0, w: 1.2, h: 8, sx: { background: `rgba(${TEAL},0.55)`, transform: 'rotate(52deg)', transformOrigin: '50% 0' } },
        { x: 7.2, y: 0, w: 1.2, h: 8, sx: { background: `rgba(${TEAL},0.55)`, transform: 'rotate(-52deg)', transformOrigin: '50% 0' } },
      ],
    },
  ],
  checked_every_face: [
    pill(3, 7, 11, 5, '#C6C3D6'),
    oval(5.5, 4, 6, 5, '#CFCCDD'),
    pill(18, 6, 12, 5.5, '#AEBFC9'),
    hatched(19, 11, 10, 6, 96, '150,175,190', 0.7),
    bar(34, 6, 11, 'rgba(190,167,120,0.95)'),
    bar(36, 9, 9, 'rgba(190,167,120,0.8)'),
    bar(34, 12, 7, 'rgba(190,167,120,0.62)'),
    { x: 21, y: 6, w: 6, h: 13, sx: { background: `linear-gradient(rgba(${TEAL},0.05), rgba(${TEAL},0.42))`, clipPath: 'polygon(36% 0, 64% 0, 82% 100%, 18% 100%)', transform: 'rotate(14deg)', transformOrigin: '50% 100%' } },
  ],
  carried_their_feelings: [
    pill(24, 2, 16, 6, '#6E79A4'),
    oval(28, 0, 8, 5, '#7E88AF'),
    hatched(25, 7, 14, 6, 100, '110,121,164', 0.55),
    pill(6, 11, 11, 4.5, '#6E79A4'),
    oval(8, 9, 6, 4, '#7E88AF'),
    hatched(7, 15, 9, 4, 100, '110,121,164', 0.5),
  ],
  held_it_in: [
    { x: 12, y: 3, w: 22, h: 13, sx: { borderRadius: '4px', border: '1.5px solid #8A93A8', boxSizing: 'border-box' } },
    pill(17, 9, 12, 4.5, '#9FB4C4'),
    oval(20, 6.5, 6, 4.5, '#AEC1CF'),
  ],
  got_stuck_and_dug_in: [
    { x: 15, y: 7, w: 13, h: 13, img: true, sx: { opacity: 0.92 } },
    { x: 24, y: 17, w: 1, h: 8, sx: { background: '#6E6258' } },
    { x: 21, y: 23, w: 6, h: 7, glyph: '⚓', sx: { fontSize: '6.5px', lineHeight: 1, color: '#4C5A66' } },
  ],
  drifted_into_fog: [
    { x: 19, y: 3, w: 9, h: 9, sx: { borderRadius: '50%', background: 'radial-gradient(circle, #F6DEB4 0 45%, rgba(246,222,180,0.4) 45% 72%, transparent 78%)' } },
    { x: 4, y: 14, w: 10, h: 5, sx: { borderRadius: '3px 3px 0 0', background: '#93A2A8' } },
    { x: 33, y: 13, w: 11, h: 6, sx: { borderRadius: '3px 3px 0 0', background: '#A6B2B6' } },
    { x: 0, y: 0, w: 48, h: 30, sx: { background: 'rgba(214,208,222,0.55)' } },
  ],
  changed_the_subject: [
    pill(4, 4, 13, 5.5, '#8B98A8'),
    oval(7, 2, 7, 4.5, '#9AA6B4'),
    hatched(5, 9, 11, 5, 100, '139,152,168', 0.55),
    { x: 30, y: 4, w: 10, h: 10, sx: { borderRadius: '50%', background: 'radial-gradient(circle, #F6D48A 0 40%, rgba(246,212,138,0.4) 40% 66%, transparent 74%)' } },
  ],
};

export const WEATHER_SPRITE_CODES = Object.keys(SPRITES);

function Part({ piece }: { piece: Piece }) {
  const sx = { position: 'absolute', left: piece.x, top: piece.y, width: piece.w, height: piece.h, ...piece.sx } as SxProps<Theme>;
  if (piece.img) return <Box component="img" src={CANOE_SRC} alt="" sx={sx} />;
  return (
    <Box component="span" sx={sx}>
      {piece.glyph}
      {piece.children?.map((child, i) => <Part key={i} piece={child} />)}
    </Box>
  );
}

interface WeatherSpriteProps {
  code: string | undefined;
}

export default function WeatherSprite({ code }: WeatherSpriteProps) {
  const reducedMotion = useCrossingReducedMotion();
  const pieces = code ? SPRITES[code] : undefined;
  if (!code || !pieces) return null;

  return (
    <Box
      data-testid="weather-sprite-holder"
      aria-hidden="true"
      sx={{
        position: 'absolute', left: '50%', top: 0, width: 150, height: 40, marginLeft: '-75px', zIndex: 6,
        pointerEvents: 'none', filter: HALO,
        ...WR_RIDE_KEYFRAMES,
        animation: reducedMotion ? 'none' : WR_RIDE_ANIMATION,
      }}
    >
      <Box
        data-testid={`weather-sprite-${code}`}
        sx={{ position: 'absolute', left: '50%', top: 26, width: 48, height: 30, marginLeft: '-24px', transform: 'scale(0.92)', transformOrigin: '50% 0' }}
      >
        {pieces.map((piece, i) => <Part key={i} piece={piece} />)}
      </Box>
    </Box>
  );
}
